import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import seahLogo from '../assets/seah-logo.svg';
import './Layout.css';

const Layout: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedRound, setSelectedRound] = useState<string | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(false);
  const [adminMenuExpanded, setAdminMenuExpanded] = useState<boolean>(false);
  
  // PDF 모드 확인
  const urlParams = new URLSearchParams(window.location.search);
  const isPdfMode = urlParams.get('pdf') === 'true';

  // 관리자 페이지인지 확인
  const isAdminPage = location.pathname === '/admin';
  const adminCategory = isAdminPage ? (urlParams.get('category') || 'USER') : null;

  useEffect(() => {
    // localStorage에서 선택된 라운드 복원
    const savedRound = localStorage.getItem('selectedRound');
    if (savedRound) {
      setSelectedRound(savedRound);
    }
    // 관리자 페이지일 때 메뉴 자동 확장
    if (isAdminPage) {
      setAdminMenuExpanded(true);
    }
  }, [isAdminPage]);

  useEffect(() => {
    // 선택된 라운드를 localStorage에 저장
    if (selectedRound) {
      localStorage.setItem('selectedRound', selectedRound);
    } else {
      localStorage.removeItem('selectedRound');
    }
  }, [selectedRound]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className={`layout ${isPdfMode ? 'pdf-mode' : ''}`}>
      {!isPdfMode && (
        <header className="header">
          <div className="header-content">
            <h1 className="logo">
              <img src={seahLogo} alt="Seah" className="logo-image" />
              <span className="logo-text">정보보호 포탈</span>
            </h1>
            <div className="header-right">
              <div className="user-info">
                <span>{user?.email}</span>
                <span className="role-badge">{user?.role}</span>
              </div>
              <button onClick={handleLogout} className="logout-button">로그아웃</button>
            </div>
          </div>
        </header>
      )}
      <div className="layout-body">
        {!isPdfMode && (
          <aside className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
            <div className="sidebar-header">
              <button 
                className="sidebar-toggle"
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                title={sidebarCollapsed ? '사이드바 확장' : '사이드바 최소화'}
              >
                {sidebarCollapsed ? '→' : '←'}
              </button>
            </div>
            <nav className="sidebar-nav">
              <div className="nav-section">
                <h3 className="nav-section-title">대시보드</h3>
                <ul className="nav-menu">
                  <li>
                    <button
                      className={location.pathname === '/dashboard' || location.pathname === '/dashboard/apt' ? 'active' : ''}
                      onClick={() => navigate('/dashboard/apt')}
                      title="악성메일 모의훈련"
                    >
                      <span className="menu-icon">📧</span>
                      <span className="menu-text">악성메일 모의훈련</span>
                    </button>
                  </li>
                </ul>
              </div>
              <div className="nav-section">
                <h3 className="nav-section-title">운영보고서</h3>
                <ul className="nav-menu">
                  <li>
                    <button
                      className={location.pathname === '/dashboard/dlp' ? 'active' : ''}
                      onClick={() => navigate('/dashboard/dlp')}
                      title="DLP 운영보고서"
                    >
                      <span className="menu-icon">🔒</span>
                      <span className="menu-text">DLP 운영보고서</span>
                    </button>
                  </li>
                  <li>
                    <button
                      className={location.pathname === '/dashboard/nac' ? 'active' : ''}
                      onClick={() => navigate('/dashboard/nac')}
                      title="NAC 운영보고서"
                    >
                      <span className="menu-icon">🛡️</span>
                      <span className="menu-text">NAC 운영보고서</span>
                    </button>
                  </li>
                </ul>
              </div>
              {(user?.role === 'admin' || user?.role === 'editor' || user?.role === 'reviewer') && (
                <div className="nav-section">
                  <h3 className="nav-section-title">관리</h3>
                  <ul className="nav-menu">
                    <li>
                      <button
                        className={`nav-menu-parent ${isAdminPage ? 'active' : ''} ${adminMenuExpanded ? 'expanded' : ''}`}
                        onClick={() => {
                          setAdminMenuExpanded(!adminMenuExpanded);
                          if (!isAdminPage) {
                            navigate('/admin?category=USER');
                          }
                        }}
                        title="관리자 페이지"
                      >
                        <span className="menu-icon">⚙️</span>
                        <span className="menu-text">관리자 페이지</span>
                        <span className="menu-arrow">{adminMenuExpanded ? '▼' : '▶'}</span>
                      </button>
                      {adminMenuExpanded && !sidebarCollapsed && (
                        <ul className="nav-submenu">
                          <li>
                            <button
                              className={adminCategory === 'USER' ? 'active' : ''}
                              onClick={() => navigate('/admin?category=USER')}
                              title="유저관리"
                            >
                              <span className="menu-icon">👤</span>
                              <span className="menu-text">USER</span>
                            </button>
                          </li>
                          <li>
                            <button
                              className={adminCategory === 'APT' ? 'active' : ''}
                              onClick={() => navigate('/admin?category=APT')}
                              title="APT 스팸신고데이터"
                            >
                              <span className="menu-icon">📧</span>
                              <span className="menu-text">APT</span>
                            </button>
                          </li>
                          <li>
                            <button
                              className={adminCategory === 'DLP' ? 'active' : ''}
                              onClick={() => navigate('/admin?category=DLP&tab=dlp-report')}
                              title="DLP 운영보고서 관리"
                            >
                              <span className="menu-icon">🔒</span>
                              <span className="menu-text">DLP</span>
                            </button>
                          </li>
                          <li>
                            <button
                              className={adminCategory === 'NAC' ? 'active' : ''}
                              onClick={() => navigate('/admin?category=NAC&tab=nac-report')}
                              title="NAC 운영보고서 관리"
                            >
                              <span className="menu-icon">🛡️</span>
                              <span className="menu-text">NAC</span>
                            </button>
                          </li>
                        </ul>
                      )}
                    </li>
                  </ul>
                </div>
              )}
            </nav>
          </aside>
        )}
        <main className="main-content">
          <Outlet context={{ selectedRound }} />
        </main>
      </div>
    </div>
  );
};

export default Layout;

